import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { useTranslation } from 'react-i18next';
import { compactGameMetaLine } from './games/gameFormat';

const numberOrNull = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const gameForMeta = (game) => ({
  minPlayers: numberOrNull(game.minPlayers ?? game.minplayers),
  maxPlayers: numberOrNull(game.maxPlayers ?? game.maxplayers),
  playingTime: numberOrNull(game.playingTime ?? game.playingtime),
  minPlayTime: numberOrNull(game.minPlayTime ?? game.minplaytime),
  maxPlayTime: numberOrNull(game.maxPlayTime ?? game.maxplaytime),
});

const coverUrl = (game) => game.thumbnail || game.image || '';

const BGGResultCard = ({ game, onAdd, isDisabled = false }) => {
  const { t } = useTranslation();
  const [imageFailed, setImageFailed] = useState(false);
  const title = game.name || t('bgg.result.unknownTitle');
  const year = game.yearpublished || '';
  const primaryTag = game.categories?.[0] || game.mechanics?.[0] || '';
  const cover = imageFailed ? '' : coverUrl(game);
  const meta = compactGameMetaLine(gameForMeta(game), t);
  const secondaryParts = [
    year ? t('bgg.result.yearPublished', { year }) : t('bgg.result.unknownYear'),
    primaryTag,
    'BoardGameGeek',
  ].filter(Boolean);

  return (
    <Card
      component="button"
      type="button"
      variant="outlined"
      disabled={isDisabled || !onAdd}
      onClick={() => onAdd?.(game)}
      sx={{
        width: '100%',
        display: 'grid',
        gridTemplateColumns: { xs: '58px minmax(0, 1fr)', sm: '68px minmax(0, 1fr) auto' },
        alignItems: 'center',
        gap: { xs: 1.25, sm: 1.5 },
        p: { xs: 1.25, sm: 1.5 },
        borderRadius: 2,
        bgcolor: 'rgba(255,250,239,0.96)',
        color: 'text.primary',
        cursor: isDisabled || !onAdd ? 'default' : 'pointer',
        font: 'inherit',
        textAlign: 'left',
        transition: 'border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease',
        '&:hover': {
          borderColor: isDisabled ? 'divider' : 'primary.main',
          boxShadow: isDisabled ? 0 : '0 16px 26px -24px rgba(6, 37, 34, 0.86)',
        },
        '&:focus-visible': {
          outline: '3px solid',
          outlineColor: 'secondary.light',
          outlineOffset: 3,
        },
        '&:disabled': {
          opacity: 0.68,
        },
      }}
    >
      <Box
        sx={{
          width: { xs: 58, sm: 68 },
          height: { xs: 76, sm: 90 },
          borderRadius: 1.25,
          overflow: 'hidden',
          bgcolor: 'rgba(15, 76, 69, 0.08)',
          border: '1px solid rgba(15, 76, 69, 0.14)',
          display: 'grid',
          placeItems: 'center',
          color: 'text.secondary',
          boxShadow: cover ? '0 12px 18px -18px rgba(6, 37, 34, 0.78)' : 0,
        }}
      >
        {cover ? (
          <Box
            component="img"
            src={cover}
            alt={t('bgg.result.coverAlt', { title })}
            loading="lazy"
            decoding="async"
            width="68"
            height="90"
            onError={() => setImageFailed(true)}
            sx={{
              display: 'block',
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
        ) : (
          <ImageOutlinedIcon aria-hidden="true" />
        )}
      </Box>

      <CardContent sx={{ minWidth: 0, p: 0, '&:last-child': { pb: 0 } }}>
        <Typography
          variant="h3"
          sx={{
            display: '-webkit-box',
            overflow: 'hidden',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflowWrap: 'anywhere',
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {meta}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: 'block',
            mt: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {secondaryParts.join(' - ')}
        </Typography>
      </CardContent>

      {onAdd ? (
        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          justifyContent={{ xs: 'flex-start', sm: 'center' }}
          sx={{
            gridColumn: { xs: '1 / -1', sm: 'auto' },
            justifySelf: { xs: 'stretch', sm: 'end' },
            color: 'primary.main',
            fontWeight: 900,
            px: { xs: 0, sm: 1 },
          }}
        >
          <AddIcon fontSize="small" />
          <Typography component="span" variant="button">
            {t('bgg.result.addToCollection')}
          </Typography>
        </Stack>
      ) : null}
    </Card>
  );
};

export default BGGResultCard;
